import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"

// Controls the job mismatch modal for trade-day assignments.
// Uses native <dialog> element with showModal() for proper top-layer rendering.
// Supports both single date (date), multiple dates (dates as JSON array), and date ranges.
export default class extends Controller {
  static values = {
    taskId: Number,
    tradeId: Number,
    employeeId: Number,
    date: String,
    dates: Array, // Array of date strings for multi-date assignments
    startDate: String, // For range assignments
    endDate: String, // For range assignments
    isRange: Boolean, // Whether this is a range assignment
  }

  connect() {
    // Show dialog as modal
    this.element.showModal()

    // Handle native close event
    this.boundHandleClose = this.handleClose.bind(this)
    this.element.addEventListener("close", this.boundHandleClose)

    // Close when clicking the backdrop
    this.boundHandleClick = this.handleClick.bind(this)
    this.element.addEventListener("click", this.boundHandleClick)

    // Show date count if multiple dates are selected
    if (this.hasDatesValue && this.datesValue.length > 1) {
      this.showDateCount()
    }
  }

  showDateCount() {
    const count = this.datesValue.length
    const infoBox = this.element.querySelector(".bg-yellow-50.rounded-lg")
    if (infoBox) {
      const dateCountDiv = document.createElement("div")
      dateCountDiv.className =
        "mt-3 p-2 bg-blue-50 rounded border border-blue-200 text-sm text-blue-800"
      dateCountDiv.innerHTML = `<strong>${count} Tage</strong> werden zugewiesen.`
      infoBox.after(dateCountDiv)
    }
  }

  disconnect() {
    this.element.removeEventListener("close", this.boundHandleClose)
    this.element.removeEventListener("click", this.boundHandleClick)
  }

  handleClose() {
    // Remove from DOM after dialog closes
    this.element.remove()
  }

  handleClick(event) {
    // Close when clicking the backdrop
    if (event.target === this.element) {
      this.close()
    }
  }

  close() {
    this.element.close()
  }

  async proceed() {
    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    // Close modal first
    this.close()

    try {
      // Check if this is a range assignment (from drag-drop)
      if (this.isRangeValue && this.hasStartDateValue && this.hasEndDateValue) {
        await this.proceedWithRange(csrfToken)
      } else {
        await this.proceedWithDates(csrfToken)
      }

      // Dispatch event to refresh the trade drawer if it's open
      document.dispatchEvent(
        new CustomEvent("plantafel:trade-assignment-changed", {
          detail: { taskId: this.taskIdValue, tradeId: this.tradeIdValue },
        }),
      )
    } catch (error) {
      Honeybadger.notify(error, {
        context: { component: "plantafel-trade-mismatch-modal", action: "force_assign" },
      })
    }
  }

  async proceedWithRange(csrfToken) {
    const url = `/plantafel/tasks/${this.taskIdValue}/trades/${this.tradeIdValue}/assign_range`
    const data = {
      employee_id: this.employeeIdValue,
      start_date: this.startDateValue,
      end_date: this.endDateValue,
      force: true,
      view_mode: "vertical_month",
      view_start_date: this.getViewStartDate(),
    }

    const response = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })

    const contentType = response.headers.get("Content-Type") || ""
    const html = await response.text()

    if (contentType.includes("text/vnd.turbo-stream.html")) {
      Turbo.renderStreamMessage(html)
    } else if (!response.ok) {
      Honeybadger.notify(`Failed to force assign employee to trade range: ${response.status}`, {
        context: {
          level: "error",
          component: "plantafel-trade-mismatch-modal",
          action: "force_assign_range",
        },
      })
    }
  }

  async proceedWithDates(csrfToken) {
    const url = `/plantafel/tasks/${this.taskIdValue}/trades/${this.tradeIdValue}/assign`

    // Get dates - either from dates array or single date
    const dates =
      this.hasDatesValue && this.datesValue.length > 0 ? this.datesValue : [this.dateValue]

    // Process all dates with force=true
    for (let i = 0; i < dates.length; i++) {
      const date = dates[i]
      const data = {
        employee_id: this.employeeIdValue,
        date: date,
        force: true,
        view_mode: "vertical_month",
        view_start_date: this.getViewStartDate(),
      }

      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })

      const contentType = response.headers.get("Content-Type") || ""
      const html = await response.text()

      // Only render the last response to avoid flickering
      if (i === dates.length - 1 && contentType.includes("text/vnd.turbo-stream.html")) {
        Turbo.renderStreamMessage(html)
      } else if (!response.ok) {
        Honeybadger.notify(`Failed to force assign employee to trade-day: ${response.status}`, {
          context: {
            level: "error",
            component: "plantafel-trade-mismatch-modal",
            action: "force_assign",
          },
        })
      }

      // Small delay between requests
      if (i < dates.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 50))
      }
    }
  }

  getViewStartDate() {
    const urlParams = new URLSearchParams(window.location.search)
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, "0")
    const day = String(now.getDate()).padStart(2, "0")
    return urlParams.get("date") || `${year}-${month}-${day}`
  }
}
